

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;1,400&family=DM+Sans:wght@300;400;500&display=swap');

  :root {
    --leaf: #2d6a4f;
    --leaf-light: #40916c;
    --lime: #95d5b2;
    --cream: #fefae0;
    --gold: #d4a017;
    --dark: #1b2a23;
    --muted: #6b7c73;
    --card-bg: #ffffff;
    --shadow: 0 8px 40px rgba(45,106,79,0.10);
  }

  .about-page * { box-sizing: border-box; margin: 0; padding: 0; }
  .about-page {
    font-family: 'DM Sans', sans-serif;
    background: var(--cream);
    color: var(--dark);
    overflow-x: hidden;
  }

  /* ── HERO ── */
  .hero {
    position: relative;
    min-height: 62vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 80px 24px 60px;
    background: linear-gradient(160deg, #1b2a23 0%, #2d6a4f 60%, #40916c 100%);
    overflow: hidden;
  }
  .hero::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      radial-gradient(ellipse 60% 40% at 20% 50%, rgba(149,213,178,0.12) 0%, transparent 70%),
      radial-gradient(ellipse 40% 60% at 80% 30%, rgba(212,160,23,0.08) 0%, transparent 70%);
  }
  .hero-badge {
    display: inline-block;
    background: rgba(212,160,23,0.18);
    border: 1px solid rgba(212,160,23,0.45);
    color: #f0c040;
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 3px;
    text-transform: uppercase;
    padding: 6px 20px;
    border-radius: 40px;
    margin-bottom: 28px;
    animation: fadeUp 0.6s ease both;
  }
  .hero h1 {
    font-family: 'Playfair Display', serif;
    font-size: clamp(2.8rem, 7vw, 5.2rem);
    font-weight: 700;
    color: #fff;
    line-height: 1.1;
    letter-spacing: -1px;
    animation: fadeUp 0.7s 0.1s ease both;
  }
  .hero h1 em { font-style: italic; color: var(--lime); }
  .hero-sub {
    max-width: 520px;
    margin: 22px auto 0;
    font-size: 1.05rem;
    font-weight: 300;
    color: rgba(255,255,255,0.72);
    line-height: 1.7;
    animation: fadeUp 0.7s 0.2s ease both;
  }
  .hero-bg-img {
    position: absolute; inset: 0;
    width: 100%; height: 100%;
    object-fit: cover; opacity: 0.13;
    mix-blend-mode: luminosity; z-index: 0;
  }
  .hero > *:not(.hero-bg-img):not(.hero-circle) { position: relative; z-index: 2; }
  .hero-fruit {
    position: absolute;
    border-radius: 50%; overflow: hidden;
    border: 3px solid rgba(255,255,255,0.15);
    box-shadow: 0 8px 32px rgba(0,0,0,0.3);
    animation: floatFruit 6s ease-in-out infinite; z-index: 1;
  }
  .hero-fruit img { width: 100%; height: 100%; object-fit: cover; display: block; }
  .hero-fruit.f1 { width: 100px; height: 100px; top: 12%; left: 5%; animation-delay: 0s; }
  .hero-fruit.f2 { width: 70px; height: 70px; top: 65%; left: 8%; animation-delay: 1.5s; }
  .hero-fruit.f3 { width: 85px; height: 85px; top: 10%; right: 6%; animation-delay: 0.8s; }
  .hero-fruit.f4 { width: 60px; height: 60px; top: 70%; right: 10%; animation-delay: 2.2s; }
  @media(max-width:600px){
    .hero-fruit.f1,.hero-fruit.f2 { display: none; }
    .hero-fruit.f3 { width: 60px; height: 60px; }
    .hero-fruit.f4 { width: 44px; height: 44px; }
  }
  .hero-circle {
    position: absolute; border-radius: 50%;
    border: 1px solid rgba(149,213,178,0.18);
    animation: spin 18s linear infinite;
  }
  .hero-circle.c1 { width: 340px; height: 340px; top: -80px; right: -80px; }
  .hero-circle.c2 { width: 220px; height: 220px; bottom: 30px; left: -60px; animation-direction: reverse; animation-duration: 22s; }
  .hero-circle.c3 { width: 120px; height: 120px; bottom: 80px; right: 15%; animation-duration: 14s; border-color: rgba(212,160,23,0.2); }

  /* ── SECTION ── */
  .section { padding: 80px 24px; max-width: 1100px; margin: 0 auto; }
  .section-label {
    text-align: center; font-size: 11px; letter-spacing: 3.5px;
    text-transform: uppercase; color: var(--leaf); font-weight: 500; margin-bottom: 10px;
  }
  .section-title {
    font-family: 'Playfair Display', serif;
    font-size: clamp(1.8rem, 4vw, 2.8rem);
    font-weight: 700; text-align: center; color: var(--dark); margin-bottom: 50px;
  }

  /* ── MISSION/VISION ── */
  .mv-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 28px; }
  @media(max-width:640px){ .mv-grid { grid-template-columns: 1fr; } }
  .mv-card {
    position: relative; background: var(--card-bg); border-radius: 20px;
    padding: 44px 40px; box-shadow: var(--shadow); overflow: hidden;
    transition: transform 0.3s ease, box-shadow 0.3s ease;
  }
  .mv-card:hover { transform: translateY(-6px); box-shadow: 0 20px 60px rgba(45,106,79,0.16); }
  .mv-card::before {
    content: ''; position: absolute; top: 0; left: 0; right: 0; height: 4px;
    background: linear-gradient(90deg, var(--leaf), var(--lime));
  }
  .mv-card.vision::before { background: linear-gradient(90deg, var(--gold), #f0c040); }
  .mv-icon {
    width: 52px; height: 52px; border-radius: 14px;
    display: flex; align-items: center; justify-content: center;
    font-size: 24px; margin-bottom: 22px;
  }
  .mv-card .mv-icon { background: rgba(45,106,79,0.10); }
  .mv-card.vision .mv-icon { background: rgba(212,160,23,0.12); }
  .mv-card h3 {
    font-family: 'Playfair Display', serif; font-size: 1.4rem;
    font-weight: 700; margin-bottom: 14px; color: var(--dark);
  }
  .mv-card p { font-size: 0.97rem; color: var(--muted); line-height: 1.75; font-weight: 300; }
  .mv-card-img {
    width: 100%; height: 180px; object-fit: cover;
    border-radius: 12px; margin-bottom: 22px; display: block;
  }

  /* ── STATS ── */
  .stats-strip {
    background: linear-gradient(135deg, var(--leaf) 0%, var(--leaf-light) 100%);
    padding: 56px 24px;
  }
  .stats-inner {
    max-width: 900px; margin: 0 auto;
    display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; text-align: center;
  }
  @media(max-width:540px){ .stats-inner { grid-template-columns: 1fr; } }
  .stat-num {
    font-family: 'Playfair Display', serif;
    font-size: clamp(2rem, 5vw, 3.2rem); font-weight: 700; color: #fff; line-height: 1;
  }
  .stat-label { font-size: 0.85rem; color: rgba(255,255,255,0.72); margin-top: 8px; letter-spacing: 1px; text-transform: uppercase; }

  /* ── GALLERY ── */
  .gallery-strip { padding: 0 24px 80px; max-width: 1100px; margin: 0 auto; }
  .gallery-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; }
  @media(max-width:700px){ .gallery-grid { grid-template-columns: repeat(2,1fr); } }
  .gallery-item {
    position: relative; border-radius: 16px; overflow: hidden;
    aspect-ratio: 1; cursor: pointer; box-shadow: var(--shadow);
  }
  .gallery-item img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.5s ease; display: block; }
  .gallery-item:hover img { transform: scale(1.08); }
  .gallery-item .g-label {
    position: absolute; bottom: 0; left: 0; right: 0;
    background: linear-gradient(0deg, rgba(27,42,35,0.85) 0%, transparent 100%);
    color: #fff; font-size: 0.82rem; font-weight: 500; letter-spacing: 1px;
    text-transform: uppercase; padding: 20px 14px 12px;
    transform: translateY(4px); transition: transform 0.3s;
  }
  .gallery-item:hover .g-label { transform: translateY(0); }

  /* ── TEAM ── */
  .team-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 24px; }
  @media(max-width:900px){ .team-grid { grid-template-columns: repeat(2,1fr); } }
  @media(max-width:480px){ .team-grid { grid-template-columns: 1fr; } }
  .team-card {
    background: var(--card-bg); border-radius: 20px; text-align: center;
    box-shadow: var(--shadow); transition: transform 0.3s, box-shadow 0.3s;
    position: relative; overflow: hidden;
  }
  .team-card:hover { transform: translateY(-8px); box-shadow: 0 24px 60px rgba(45,106,79,0.22); }
  .team-card::after {
    content: ''; position: absolute; bottom: 0; left: 0; right: 0; height: 3px;
    background: linear-gradient(90deg, var(--leaf), var(--lime));
    transform: scaleX(0); transform-origin: left; transition: transform 0.35s ease;
  }
  .team-card:hover::after { transform: scaleX(1); }
  .team-photo-wrap { position: relative; width: 100%; height: 400px; overflow: hidden; }
  .team-photo-wrap img {
    width: 100%; object-fit: contain;
    object-position: top ; transition: transform 0.5s ease; display: block;
  }
    .team-card {
  max-width: 100%;
  height:500px;
  

}
  .team-card:hover .team-photo-wrap img { transform: scale(1.07); }
  .team-photo-wrap::after {
    content: ''; position: absolute; bottom: 0; left: 0; right: 0; height: 55%;
    background: linear-gradient(0deg, rgba(27,42,35,0.5) 0%, transparent 100%);
  }
  .team-info { padding: 20px 18px 22px; }
  .team-name {
    font-family: 'Playfair Display', serif; font-size: 1.08rem;
    font-weight: 700; color: var(--dark); margin-bottom: 6px;
  }
  .team-role { font-size: 0.78rem; color: var(--leaf); font-weight: 500; letter-spacing: 1.4px; text-transform: uppercase; }

  /* ── CONTACT ── */
  .contact-strip { background: var(--dark); padding: 70px 24px; text-align: center; }
  .contact-strip h2 {
    font-family: 'Playfair Display', serif;
    font-size: clamp(1.7rem, 4vw, 2.6rem); color: #fff; margin-bottom: 14px;
  }
  .contact-strip p { color: rgba(255,255,255,0.6); font-weight: 300; margin-bottom: 30px; font-size: 1rem; }
  .contact-pills { display: flex; gap: 16px; justify-content: center; flex-wrap: wrap; }
  .contact-pill {
    display: flex; align-items: center; gap: 10px;
    background: rgba(255,255,255,0.07); border: 1px solid rgba(255,255,255,0.12);
    border-radius: 50px; padding: 12px 28px; color: #fff;
    font-size: 0.92rem; font-weight: 400;
    transition: background 0.2s, border-color 0.2s; text-decoration: none;
  }
  .contact-pill:hover { background: rgba(149,213,178,0.12); border-color: var(--lime); color: var(--lime); }
  .contact-pill span { font-size: 18px; }

  /* ── ANIMATIONS ── */
  @keyframes floatFruit {
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    50%       { transform: translateY(-14px) rotate(4deg); }
  }
  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(28px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes spin { to { transform: rotate(360deg); } }
  .fade-in { animation: fadeUp 0.65s ease both; }
  .delay-1 { animation-delay: 0.1s; }
  .delay-2 { animation-delay: 0.2s; }
  .delay-3 { animation-delay: 0.3s; }
  .delay-4 { animation-delay: 0.4s; }
/* 🔥 RESPONSIVE FIX START */

/* 🔥 RESPONSIVE FIX START */

/* Tablet */
@media (max-width: 900px) {
  .team-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .gallery-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

/* Mobile */
@media (max-width: 768px) {

  .section {
    padding: 50px 16px;
  }

  .section-title {
    font-size: 1.6rem;
    margin-bottom: 30px;
  }

  .hero {
    padding: 60px 16px 40px;
  }

  .hero h1 {
    font-size: 2.2rem;
  }

  .hero-sub {
    font-size: 0.9rem;
  }

  .mv-grid {
    grid-template-columns: 1fr;
  }

  .mv-card {
    padding: 24px 20px;
  }

  /* 🔥 CARD IMAGE */
  .mv-card-img {
    height: auto;
    object-fit: cover;
  }

  /* 🔥 TEAM BEST FIX (RECOMMENDED) */
  .team-photo-wrap {
    height: 240px;
    overflow: hidden;
  }

  .team-photo-wrap img {
    width: 100%;
    height: 100%;
    object-fit: cover;        /* 🔥 full fill */
    object-position: top;     /* 🔥 face visible */
  }

  /* HERO BG */
  .hero-bg-img {
    object-fit: cover;
  }

  .contact-strip {
    padding: 50px 16px;
  }

  .contact-pill {
    width: 100%;
    justify-content: center;
  }
}

/* Small Mobile */
@media (max-width: 500px) {

  .team-grid {
    grid-template-columns: 1fr;
  }

  .gallery-grid {
    grid-template-columns: 1fr;
  }

  /* 🔥 GALLERY */
  .gallery-item img {
    object-fit: cover;
  }

  .hero h1 {
    font-size: 1.8rem;
  }
}`

/* 🔥 RESPONSIVE FIX END */

;

const team = [
  { name: "Md Aftab Alam",  role: "Founder & CEO",       img: "https://res.cloudinary.com/dwj0k0vf9/image/upload/f_auto,q_auto/Screenshot_2026-04-06_225554_ic29rj" },
  
  { name: " Md Sajjid Aftab",   role: "Operations Manager",   img: "https://res.cloudinary.com/dmqb6h3ys/image/upload/f_auto,q_auto/Screenshot_2026-04-03_203443_x5vusj" },

];

const About = () => {
  return (
    <div className="about-page">
      <style>{styles}</style>

      {/* ── HERO ── */}
      <section className="hero">
        <img className="hero-bg-img" src="https://images.unsplash.com/photo-1519996529931-28324d5a630e?w=1400&q=60" alt="" />
        <div className="hero-fruit f1"><img src="https://images.unsplash.com/photo-1550258987-190a2d41a8ba?w=200&q=70" alt="mango" /></div>
        <div className="hero-fruit f2"><img src="https://images.unsplash.com/photo-1502741338009-cac2772e18bc?w=200&q=70" alt="grapes" /></div>
        <div className="hero-fruit f3"><img src="https://images.unsplash.com/photo-1571771894821-ce9b6c11b08e?w=200&q=70" alt="strawberry" /></div>
        <div className="hero-fruit f4"><img src="https://images.unsplash.com/photo-1587132137056-bfbf0166836e?w=200&q=70" alt="kiwi" /></div>
        <div className="hero-circle c1" />
        <div className="hero-circle c2" />
        <div className="hero-circle c3" />
        <div className="hero-badge">🌿 Farm to Doorstep</div>
        <h1>We Bring Nature's<br /><em>Finest Fruits</em><br />to You</h1>
        <p className="hero-sub">
          Dedicated to delivering farm-fresh quality with transparency, care, and a passion for sustainable living.
        </p>
      </section>

      {/* ── MISSION / VISION ── */}
      <section className="section">
        <p className="section-label">What drives us</p>
        <h2 className="section-title">Our Purpose</h2>
        <div className="mv-grid">
          <div className="mv-card fade-in delay-1">
            <img className="mv-card-img" src="https://images.unsplash.com/photo-1610832958506-aa56368176cf?w=600&q=70" alt="Fresh fruits mission" />
            <div className="mv-icon">🎯</div>
            <h3>Our Mission</h3>
            <p>To provide the freshest fruits directly from farms to your doorstep, maintaining uncompromising quality and a commitment to sustainability at every step.</p>
          </div>
          <div className="mv-card vision fade-in delay-2">
            <img className="mv-card-img" src="https://images.unsplash.com/photo-1464965911861-746a04b4bca6?w=600&q=70" alt="Farm vision" />
            <div className="mv-icon">✨</div>
            <h3>Our Vision</h3>
            <p>To become the most trusted fruit supplier in the region — known for quality, transparency, and innovation that sets a new standard in fresh produce delivery.</p>
          </div>
        </div>
      </section>

      {/* ── STATS ── */}
      <div className="stats-strip">
        <div className="stats-inner">
          <div><div className="stat-num">5,000+</div><div className="stat-label">Happy Customers</div></div>
          <div><div className="stat-num">30+</div><div className="stat-label">Fruit Varieties</div></div>
          <div><div className="stat-num">100%</div><div className="stat-label">Farm Fresh</div></div>
        </div>
      </div>

      {/* ── FRUIT GALLERY ── */}
      <div className="gallery-strip">
        <p className="section-label" style={{textAlign:'center',marginBottom:'10px'}}>What we offer</p>
        <h2 className="section-title">Our Fresh Produce</h2>
        <div className="gallery-grid">
          {[
            { src: "https://images.unsplash.com/photo-1550258987-190a2d41a8ba?w=400&q=75", label: "Mangoes" },
            { src: "https://images.unsplash.com/photo-1571771894821-ce9b6c11b08e?w=400&q=75", label: "Berries" },
            { src: "https://images.unsplash.com/photo-1587132137056-bfbf0166836e?w=400&q=75", label: "Kiwi" },
            { src: "https://images.unsplash.com/photo-1547514701-42782101795e?w=400&q=75", label: "Oranges" },
          ].map((item, i) => (
            <div className={`gallery-item fade-in delay-${i+1}`} key={i}>
              <img src={item.src} alt={item.label} />
              <div className="g-label">{item.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── TEAM ── */}
      <section className="section">
        <p className="section-label">The people behind</p>
        <h2 className="section-title">Meet Our Team</h2>
        <div className="team-grid">
          {team.map((m, i) => (
            <div className={`team-card fade-in delay-${i + 1}`} key={i}>
              <div className="team-photo-wrap">
                <img src={m.img} alt={m.name} />
              </div>
              <div className="team-info">
                <div className="team-name">{m.name}</div>
                <div className="team-role">{m.role}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── CONTACT ── */}
      <div className="contact-strip">
        <h2>Let's Get in Touch</h2>
        <p>Have questions? We're always here to help — reach out anytime.</p>
        <div className="contact-pills">
          <a href="mailto:support@fruitsexport.com" className="contact-pill">
            <span>✉️</span> support@fruitsexport.com
          </a>
          <a href="tel:+919234492624" className="contact-pill">
            <span>📞</span> +91 92344 92624
          </a>
        </div>
      </div>
    </div>
  );
};

export default About;